import {IAnimation, ReelConstants, ReelView, ReelViewData, RowsIteratorCallback, SymbolSizes, SymbolsOrdering} from '@gongxigames/framework/slot';
import {Container, Sprite} from 'pixi.js';
import {GameReelSymbol} from 'src/gameReels/GameReelSymbol';
import {WolfHaven} from 'com/gongxigames/math/wolfhaven/engine/WolfHaven';
import {GameReelConstants} from 'src/gameReels/GameReelConstants';

export interface GameReelViewData extends ReelViewData {
    dimension: number;
}

export class GameReelView extends ReelView {
    protected _symbols!: GameReelSymbol[];
    protected _dimension: number = GameReelConstants.DEFAULT_DIMENSION;
    // to let old reel strip symbols move away
    protected _oldReelSetRemovingBuffer = -1;

    constructor(reelData: GameReelViewData) {
        super(reelData);
        this.dimension = reelData.dimension;
    }

    public resetReelSymbols(): void {
        this.iterateRows((rowIndex) => {
            this.setSymbol(this.makeNormalizedPosition(this._currentPosition + rowIndex), rowIndex);
        });
    }

    public setNextStrip(strip: number[]): void {
        this._oldReelSetRemovingBuffer = this.dimension;
        this._symbols.forEach(symbol => {
            symbol.previousStrip = true;
        });
        this._strip = strip;
    }

    public iterateRows(callBack: RowsIteratorCallback, visibleRows = false): void {
        const symbolsOffSet = visibleRows ? 0 : this._reelConstants.Y_ADDITIONAL_SYMBOLS_OFFSET;
        const rowAmount = visibleRows ? this._reelConstants.ROW_AMOUNT - 1 : GameReelConstants.MAX_DIMENSIONS_ROW;

        if (this._reelConstants.SYMBOLS_ORDERING === SymbolsOrdering.TOP_TO_BOTTOM) {
            for (let rowIndex = symbolsOffSet; rowIndex <= rowAmount; rowIndex++) {
                callBack(rowIndex);
            }
        } else if (this._reelConstants.SYMBOLS_ORDERING === SymbolsOrdering.BOTTOM_TO_TOP) {
            for (let rowIndex = rowAmount; rowIndex >= symbolsOffSet; rowIndex--) {
                callBack(rowIndex);
            }
        }
    }

    public startStopping(stopPositionIndex: number): void {
        if (!this._isSpinning || this._isExpectedToStop) {
            return;
        }
        const {ROW_AMOUNT, Y_ADDITIONAL_SYMBOLS_OFFSET, REEL_STOPPING_SOUND_DURATION, getReelFullStoppingTime} = this._reelConstants;

        this.updateSymbolsOrder();
        this._isExpectedToStop = true;
        this._additionalBottomResultSymbolsAmount = this.calculateAdditionalBottomResultSymbolsForStackedSymbols(stopPositionIndex);
        this._symbolsLeftToStop = ROW_AMOUNT + Math.abs(Y_ADDITIONAL_SYMBOLS_OFFSET) + this._oldReelSetRemovingBuffer + this._oldReelSetRemovingBuffer;
        this._stopPosition = stopPositionIndex;
        // start to play stopping sound exactly in moment to have it finished to the moment when the reel stops
        this._onBounceStoppingStartedCallback && this.addTimer(() => this._onBounceStoppingStartedCallback!(), getReelFullStoppingTime() - REEL_STOPPING_SOUND_DURATION, true);
    }

    protected createReel(): void {
        this._backgroundContainer = new Sprite();
        this._container.addChild(this._backgroundContainer);
        // creating additional render-buffer of symbol from both sides of the reels
        for (let rowIndex = this._reelConstants.Y_ADDITIONAL_SYMBOLS_OFFSET; rowIndex <= GameReelConstants.MAX_DIMENSIONS_ROW; rowIndex++) {
            const background = this.getSpriteFromPool('reelsBg');
            const symbol = this.createReelSymbol();

            symbol.init(this.getSpriteFromPool('symbols'));
            symbol.setSymbol(this._strip[this.makeNormalizedPosition(rowIndex + this._currentPosition)]);
            symbol.index = rowIndex;
            symbol.x = this._offsets.x;
            symbol.y = this._offsets.y + this.getYPositionForRow(rowIndex);
            this._symbols[this.rowIndexToSymbolIndex(rowIndex)] = symbol;
            this._container.addChild(symbol);
            symbol.reelSetIndex = this._container.getChildIndex(symbol);

            if (background) {
                background.x = this._offsets.x;
                background.y = this._offsets.y + this.getYPositionForRow(rowIndex);
                this._backgroundContainer.addChild(background);
            }
        }
    }

    protected addTrailAnimation(rowIndex: number): void {
        const animation = this._spineUtils.createSpineAnimation('ruby_coin', `coin_trail_${this.dimension}x5`, true);
        const symbol = this._symbols[this.rowIndexToSymbolIndex(rowIndex)];

        symbol.trailAnimation = animation;
        symbol.trailAnimationContainer = new Container();
        symbol.trailAnimationContainer.addChild(animation.container);
        symbol.updateAnimationContainerPosition();
        this._animationToBeMasked.addChild(symbol.trailAnimationContainer);
        animation.container.x = this._reelConstants.SYMBOL.width / 2;
        animation.container.y = this._reelConstants.SYMBOL.height / 2;
        animation.start();
        this.animationUpdater.add(symbol.trailAnimation);
    }

    protected updateStoppingSymbols(): void {
        super.updateStoppingSymbols();
        this.updateOldReelSetRemovingBuffer();
    }

    protected updateOldReelSetRemovingBuffer(): void {
        if (this._oldReelSetRemovingBuffer) {
            this._oldReelSetRemovingBuffer--;
        }
    }

    protected setSymbol(stripPosition: number, rowIndex: number): void {
        const previousSymbol = this._symbols[this.rowIndexToSymbolIndex(rowIndex - 1)];
        const symbol = this._symbols[this.rowIndexToSymbolIndex(rowIndex)];
        let stripValue = this._strip[stripPosition];

        if (!this._oldReelSetRemovingBuffer && previousSymbol && rowIndex < 0) {
            previousSymbol.dimension = this.dimension;
            previousSymbol.previousStrip = false;
        }
        this.setDimension(previousSymbol, symbol, this.rowIndexToSymbolIndex(rowIndex));

        if ((previousSymbol && previousSymbol.previousStrip)) {
            stripValue = previousSymbol.symbolType;
        } else {
            symbol.previousStrip = false;
        }
        symbol.setSymbol(stripValue);

        if (this.isRubySymbol(symbol.symbolType) && !this.isRubyFrameShouldBeSwitched(rowIndex)
            && this._isSpinning && this._movedOffset !== 0) {
            this.addTrailAnimation(rowIndex);
        } else {
            symbol.stopTrailAnimation();
        }

        if (this._isBlurSymbols) {
            symbol.addBlur();
        }
    }

    protected isRubyFrameShouldBeSwitched(rowIndex: number): boolean {
        return this._symbolsLeftToStop <= this._dimension
            && this._symbolsLeftToStop + rowIndex >= 0 && this._symbolsLeftToStop + rowIndex < this._dimension;
    }

    protected createSymbolHitAnimation(symbolIndex: number): IAnimation {
        if (symbolIndex === WolfHaven.Activator) {
            return this._spineUtils.createCachedAnimation('wild', `red_moon_hit_${this.dimension}x5`);
        }

        if (symbolIndex === WolfHaven.Scatter) {
            return this._spineUtils.createSpineAnimation('scatter', `hit_scatter_${this.dimension}x5`);
        }

        if (this.isRubySymbol(symbolIndex)) {
            return this._spineUtils.createSpineAnimation('ruby_coin', `coin_hit_${this.dimension}x5`);
        }

        return null!;
    }

    protected setDimension(previousSymbol: GameReelSymbol, symbol: GameReelSymbol, index: number): void {
        if (previousSymbol && previousSymbol.dimension && (!this._isStopped && this._isSpinning && this._movedOffset !== 0)) {
            symbol.dimension = previousSymbol.dimension;
        } else {
            symbol.dimension = this._dimension;
        }
    }

    protected createReelSymbol(): GameReelSymbol {
        return new GameReelSymbol();
    }

    protected initOversizeHitSymbols(): void {
        if (this._oversizesHitCount === 0) {
            this._oversizeHitSymbols.forEach((symbol) => {
                symbol && this._oversizesHitCount++;
            });

            if (this._oversizesHitCount > 0) {
                this._reelHitOversizes = [];
                this._oversizeHitContainer = new Container();
                this._animationContainer.addChild(this._oversizeHitContainer);
            }
        }

        if (this._oversizesHitCount > 0) {
            this._oversizeHitSymbols.forEach((symbolType, row) => {
                const staticSymbol = this.createReelSymbol();
                const symbolsSprite = this.getSpriteFromPool('symbols');
                const reelSymbol = this._symbols[this.rowIndexToSymbolIndex(row - 1)];

                staticSymbol.init(symbolsSprite);
                staticSymbol.setSymbol(symbolType);
                staticSymbol.dimension = this.dimension;
                reelSymbol.animationContainer = new Container();
                reelSymbol.updateTransform();
                reelSymbol.animationContainer.addChild(staticSymbol);
                reelSymbol.animationContainer.visible = false;
                this._oversizeHitContainer.addChild(reelSymbol.animationContainer);
                this._reelHitOversizes[row] = reelSymbol.animationContainer;
            });
        }
    }

    protected reorderSymbols(): void {
        const newOffset: number = this._movedOffset % this.getLowestSize().height;

        this._symbols.forEach((symbol, index) => {
            symbol.y = this._offsets.y + this.getYPositionForRow(symbol.index) + newOffset;
        });
        this._movedOffset = newOffset;
        this._backgroundContainer!.y = newOffset;
        // getting next position from strip
        this._currentPosition = this.makeNormalizedPosition(this._currentPosition - 1);
    }

    protected needToReorderSymbols(): boolean {
        return this._movedOffset >= this.getLowestSize().height;
    }

    protected getYPositionForRow(rowIndex: number): number {
        let position = 0;

        if (rowIndex < 0) {
            for (let index = -1; index >= rowIndex; index--) {
                position -= this.getSymbolSizeByDimension(this.rowIndexToSymbolIndex(index - 1)).height;
            }
        } else {
            for (let index = 0; index < rowIndex; index++) {
                position += this.getSymbolSizeByDimension(this.rowIndexToSymbolIndex(index - 1)).height;
            }
        }

        return position;
    }

    protected getSymbolSizeByDimension(rowIndex: number): SymbolSizes {
        const numberOfAllSymbols = Math.abs(this._reelConstants.Y_ADDITIONAL_SYMBOLS_OFFSET) + GameReelConstants.MAX_DIMENSIONS_ROW + 1;
        const isAllSymbolsInited = this._symbols.length >= numberOfAllSymbols;

        if (isAllSymbolsInited && rowIndex >= 0) {
            return GameReelConstants.DIMENSION_SYMBOL_SIZES[this._symbols[rowIndex].dimension];
        }

        return GameReelConstants.DIMENSION_SYMBOL_SIZES[this._dimension];
    }

    protected getLowestSize(): SymbolSizes {
        return GameReelConstants.DIMENSION_SYMBOL_SIZES[Math.min(this._symbols[0].dimension, this._dimension)];
    }

    protected updateConstantsValue(): void {
        this._reelConstants = Object.assign({}, ReelConstants, {
            ROW_AMOUNT: this.dimension,
            SYMBOL_SLOT: GameReelConstants.DIMENSION_SYMBOL_SLOT_SIZES[this._dimension],
            SYMBOL: GameReelConstants.DIMENSION_SYMBOL_SIZES[this._dimension]
            // SYMBOLS_ORDERING: SymbolsOrdering.BOTTOM_TO_TOP
        });
    }

    public get dimension(): number {
        return this._dimension;
    }

    public set dimension(value: number) {
        this._dimension = value;
        this.updateConstantsValue();
    }

    public fadeSymbol(rowIndex: number): void {
        const symbol = this._symbols[this.rowIndexToSymbolIndex(rowIndex)] as GameReelSymbol;

        symbol.fadeSymbol();
    }

    public hideTail(rowIndex: number): void {
        const symbol = this._symbols[this.rowIndexToSymbolIndex(rowIndex)] as GameReelSymbol;

        if (symbol.symbolType !== WolfHaven.Wild && symbol.symbolType !== WolfHaven.Activator) {
            symbol.visible = false;
        }
    }

    public resetSymbols(): void {
        this._symbols.forEach(symbol => (symbol as GameReelSymbol).reset());
    }

    public setWildSymbol(rowIndex: number): void {
        const symbol = this._symbols[this.rowIndexToSymbolIndex(rowIndex)] as GameReelSymbol;

        symbol.setSymbol(WolfHaven.Wild);
    }
}
